import React, { useState } from "react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import LoginIcon from "@mui/icons-material/Login";
import clsx from "clsx";
import CustomButton from "../CustomButton/CustomButton";
import { API_URL, auth } from "../../auth/client";
import { describeError, normalizeError } from "../../helper/errors";
import css from "./ErrorState.module.css";

interface ErrorStateProps {
  error: unknown;
  /** Re-runs the failed request. Omit when there is nothing to retry. */
  onRetry?: () => void | Promise<unknown>;
  /** `page` fills the view; `section` sits inline within a populated page. */
  variant?: "page" | "section";
  /** Overrides the copy derived from the error. */
  title?: string;
  description?: string;
  className?: string;
}

const KIND_ICONS = {
  unauthenticated: LockOutlinedIcon,
  permissionDenied: LockOutlinedIcon,
  network: CloudOffIcon,
} as const;

const ErrorState: React.FC<ErrorStateProps> = ({
  error,
  onRetry,
  variant = "page",
  title,
  description,
  className,
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const { kind, statusCode, errorName, errorInstanceId } =
    normalizeError(error);
  const presentation = describeError(error);
  const Icon = KIND_ICONS[kind as keyof typeof KIND_ICONS] ?? ErrorOutlineIcon;

  const signInAgain = async () => {
    await auth.signOut();
    window.location.href = API_URL + "/multipass/api/logout";
  };

  const retry = async () => {
    if (!onRetry) return;
    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
    }
  };

  const isReauthentication = presentation.action === "reauthenticate";
  const canRetry = isReauthentication || !!onRetry;

  // Support cannot trace a Foundry failure without the instance id, and the
  // user is the only one who can read it off the screen.
  const diagnostic = [
    errorName,
    statusCode !== undefined ? `HTTP ${statusCode}` : undefined,
    errorInstanceId,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={clsx(css.container, css[variant], className)}
      role="alert"
      aria-live="polite"
    >
      <Icon className={css.icon} fontSize="inherit" />
      <h2 className={css.title}>{title ?? presentation.title}</h2>
      <p className={css.description}>
        {description ?? presentation.description}
      </p>
      {canRetry && (
        <CustomButton
          onClick={isReauthentication ? signInAgain : retry}
          icon={isReauthentication ? LoginIcon : RefreshIcon}
          intent="primary"
          size="default"
          variant="filled"
          loading={isRetrying}
          className={css.action}
        >
          {presentation.actionLabel}
        </CustomButton>
      )}
      {diagnostic && <p className={css.diagnostic}>{diagnostic}</p>}
    </div>
  );
};

export default ErrorState;
